import { LitElement, html, css } from 'lit';
import { api } from '../api.js';
import { REQUIRED_FIELD_PARTS, SOIL_STANDARDS, nen6693 } from '@mv/models';

const PART_LABEL = Object.fromEntries(REQUIRED_FIELD_PARTS.map((p) => [p.id, p.label]));

const formatWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' });
};

/**
 * Verplichte velden per norm (/admin/settings/required-fields). Alleen systeembeheerders.
 *
 * De lijst komt uit @mv/models (de bijlage NEN 5104 vs NEN 6693); hier zet een beheerder een
 * regel aan of uit, altijd met een opmerking. Dat geldt voor alle bedrijven, en de eindedagcheck
 * rekent er meteen mee, ook voor eerdere dagen. Nieuwe regels komen via de code.
 */
export class AdminRequiredFields extends LitElement {
  static properties = {
    _fields: { state: true },
    _loading: { state: true },
    _standard: { state: true },
    _part: { state: true },
    _editKey: { state: true },
    _note: { state: true },
    _saving: { state: true },
    _error: { state: true },
  };

  constructor() {
    super();
    this._fields = [];
    this._loading = true;
    this._standard = 'nen6693';
    this._part = '';
    this._editKey = null;
    this._note = '';
    this._saving = false;
    this._error = '';
  }

  connectedCallback() { super.connectedCallback(); this._load(); }

  async _load() {
    this._loading = true;
    this._error = '';
    try {
      this._fields = (await api.getRequiredFieldsAdmin()).fields || [];
    } catch (e) {
      this._error = `Laden mislukt: ${e.message}`;
    } finally {
      this._loading = false;
    }
  }

  _startEdit(f) {
    this._editKey = f.key;
    this._note = '';
    this._error = '';
  }

  _cancel() { this._editKey = null; this._error = ''; }

  async _save(f) {
    const note = this._note.trim();
    // De server weigert het ook; dit scheelt een rondje en zegt meteen waarom.
    if (!note) { this._error = 'Een opmerking is verplicht: waarom wijzigt dit veld?'; return; }
    this._saving = true;
    this._error = '';
    try {
      const { field } = await api.setRequiredField(f.key, !f.required, note);
      this._fields = this._fields.map((x) => (x.key === field.key ? field : x));
      this._editKey = null;
    } catch (e) {
      this._error = e.message || 'Opslaan mislukt';
    } finally {
      this._saving = false;
    }
  }

  _workTypes(f) {
    return f.workTypes?.length ? f.workTypes.map((w) => nen6693.werksoortLabel(w) || w).join(', ') : 'Alle';
  }

  _renderRow(f) {
    const editing = this._editKey === f.key;
    return html`
      <tr class=${f.overridden ? 'changed' : ''}>
        <td>${PART_LABEL[f.part] || f.part}</td>
        <td>
          <div class="label">${f.label}</div>
          ${f.condition ? html`<div class="muted small">${f.condition}</div>` : ''}
        </td>
        <td>${SOIL_STANDARDS[f.standard]?.short || f.standard}</td>
        <td class="small">${this._workTypes(f)}</td>
        <td>
          <span class="pill ${f.required ? 'on' : 'off'}">${f.required ? 'Ja' : 'Nee'}</span>
          <div class="muted small">Bijlage: ${f.level}</div>
        </td>
        <td class="small">
          ${f.note ? html`<div>${f.note}</div>` : html`<span class="muted">—</span>`}
          ${f.updatedAt ? html`<div class="muted">${f.updatedBy || 'onbekend'}, ${formatWhen(f.updatedAt)}</div>` : ''}
        </td>
        <td class="actions">
          ${editing ? '' : html`
            <button class="btn" @click=${() => this._startEdit(f)}>
              ${f.required ? 'Niet verplicht maken' : 'Verplicht maken'}
            </button>`}
        </td>
      </tr>
      ${editing ? html`
        <tr class="edit-row">
          <td colspan="7">
            <label class="small" for="note">
              Opmerking (verplicht): waarom wordt "${f.label}" ${f.required ? 'niet verplicht' : 'verplicht'}?
            </label>
            <textarea id="note" rows="2" maxlength="500" .value=${this._note}
              @input=${(e) => { this._note = e.target.value; }}></textarea>
            ${this._error ? html`<div class="error">${this._error}</div>` : ''}
            <div class="edit-actions">
              <button class="btn" ?disabled=${this._saving} @click=${this._cancel}>Annuleren</button>
              <button class="btn primary" ?disabled=${this._saving} @click=${() => this._save(f)}>
                ${this._saving ? 'Opslaan…' : 'Opslaan'}
              </button>
            </div>
          </td>
        </tr>` : ''}
    `;
  }

  render() {
    const rows = this._fields.filter((f) => f.standard === this._standard && (!this._part || f.part === this._part));
    return html`
      <h1>Verplichte velden</h1>
      <p class="muted">
        Welke velden de eindedagcheck en de signalering als verplicht zien, per norm en werksoort.
        Een wijziging geldt voor alle bedrijven en meteen ook voor eerdere dagen. De veldapp krijgt hem bij de volgende sync.
      </p>
      <div class="filters">
        ${Object.values(SOIL_STANDARDS).map((s) => html`
          <button class="tab ${this._standard === s.id ? 'active' : ''}" @click=${() => { this._standard = s.id; this._editKey = null; }}>${s.short}</button>`)}
        <select .value=${this._part} @change=${(e) => { this._part = e.target.value; this._editKey = null; }}>
          <option value="">Alle onderdelen</option>
          ${REQUIRED_FIELD_PARTS.map((p) => html`<option value=${p.id} ?selected=${p.id === this._part}>${p.label}</option>`)}
        </select>
      </div>
      ${this._error && !this._editKey ? html`<div class="error">${this._error}</div>` : ''}
      ${this._loading ? html`<p class="muted">Laden…</p>` : html`
        <table>
          <thead>
            <tr>
              <th>Onderdeel</th><th>Veld</th><th>Norm</th><th>Werksoort</th><th>Verplicht</th><th>Opmerking / laatste wijziging</th><th></th>
            </tr>
          </thead>
          <tbody>${rows.map((f) => this._renderRow(f))}</tbody>
        </table>`}
    `;
  }

  static styles = css`
    :host { display: block; }
    h1 { font-size: 1.4rem; margin: 0 0 .5rem; color: var(--mv-text); }
    .muted { color: var(--mv-text-muted); }
    .small { font-size: .8rem; }
    .filters { display: flex; gap: .5rem; align-items: center; margin: 1rem 0; flex-wrap: wrap; }
    .tab {
      border: 1px solid var(--mv-border, #e4e6ef); background: var(--mv-surface, #fff); cursor: pointer;
      padding: 6px 12px; border-radius: 8px; font-weight: 600; color: var(--mv-text);
    }
    .tab.active { background: var(--mv-primary); border-color: var(--mv-primary); color: #fff; }
    select { padding: 6px 8px; border-radius: 8px; border: 1px solid var(--mv-border, #e4e6ef); }
    table { width: 100%; border-collapse: collapse; font-size: .88rem; }
    th, td { text-align: left; padding: 8px 10px; border-bottom: 1px solid var(--mv-border, #eff2f5); vertical-align: top; }
    th { font-size: .72rem; text-transform: uppercase; letter-spacing: .04em; color: var(--mv-text-muted); }
    tr.changed td:first-child { box-shadow: inset 3px 0 0 var(--mv-primary); }
    .label { font-weight: 600; color: var(--mv-text); }
    .pill { display: inline-block; padding: 2px 10px; border-radius: 999px; font-weight: 700; font-size: .78rem; }
    .pill.on { background: var(--mv-primary-wash, #e9f1ea); color: var(--mv-primary-ink, #0d5c2b); }
    .pill.off { background: rgba(0,0,0,.05); color: var(--mv-text-muted); }
    .actions { text-align: right; white-space: nowrap; }
    .btn {
      border: 1px solid var(--mv-border, #e4e6ef); background: var(--mv-surface, #fff); cursor: pointer;
      padding: 5px 10px; border-radius: 8px; font-size: .8rem; font-weight: 600; color: var(--mv-text);
    }
    .btn.primary { background: var(--mv-primary); border-color: var(--mv-primary); color: #fff; }
    .btn:disabled { opacity: .6; cursor: default; }
    .edit-row td { background: var(--mv-surface-alt, rgba(0,0,0,.02)); }
    textarea { width: 100%; box-sizing: border-box; margin-top: 4px; padding: 6px 8px; border-radius: 8px; border: 1px solid var(--mv-border, #e4e6ef); font: inherit; }
    .edit-actions { display: flex; gap: .5rem; justify-content: flex-end; margin-top: .5rem; }
    .error { color: #b42318; font-size: .85rem; margin: .4rem 0; }
  `;
}

customElements.define('admin-required-fields', AdminRequiredFields);
